import React, { useState } from 'react';
import { ProcessDefinition, ProcessValidationResult } from '../../types/processDefinition';
import { ProcessParametersDialog } from './ProcessParametersDialog';
import './ProcessDefinitionHeader.css';

interface ProcessDefinitionHeaderProps {
  process: ProcessDefinition;
  onProcessUpdate: (updatedProcess: Partial<ProcessDefinition>) => void;
  validationResults: ProcessValidationResult | null;
  onToggleValidation: () => void;
  onToggleNavigation: () => void;
}

export const ProcessDefinitionHeader: React.FC<ProcessDefinitionHeaderProps> = ({
  process,
  onProcessUpdate,
  validationResults,
  onToggleValidation,
  onToggleNavigation,
}) => {
  const [showParametersDialog, setShowParametersDialog] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState(process.name);
  const [editedDescription, setEditedDescription] = useState(process.description);

  const handleSave = () => {
    onProcessUpdate({
      name: editedName,
      description: editedDescription,
    });
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditedName(process.name);
    setEditedDescription(process.description);
    setIsEditing(false);
  };

  const getValidationIcon = () => {
    if (!validationResults) return null;

    if (validationResults.error_count > 0) {
      return <span className="validation-icon error">⚠️</span>;
    } else if (validationResults.warning_count > 0) {
      return <span className="validation-icon warning">⚠️</span>;
    } else {
      return <span className="validation-icon success">✅</span>;
    }
  };

  const getValidationText = () => {
    if (!validationResults) return 'No validation data';

    if (validationResults.error_count > 0) {
      return `${validationResults.error_count} error${validationResults.error_count > 1 ? 's' : ''}`;
    } else if (validationResults.warning_count > 0) {
      return `${validationResults.warning_count} warning${validationResults.warning_count > 1 ? 's' : ''}`;
    } else {
      return 'All validations passed';
    }
  };

  return (
    <div className="process-definition-header">
      <div className="header-main">
        <div className="process-info">
          {isEditing ? (
            <div className="editing-controls">
              <input
                type="text"
                value={editedName}
                onChange={(e) => setEditedName(e.target.value)}
                className="process-name-input"
                placeholder="Process Name"
              />
              <textarea
                value={editedDescription}
                onChange={(e) => setEditedDescription(e.target.value)}
                className="process-description-input"
                placeholder="Process Description"
                rows={2}
              />
              <div className="editing-actions">
                <button onClick={handleSave} className="save-btn">
                  Save
                </button>
                <button onClick={handleCancel} className="cancel-btn">
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="process-display">
              <h1 className="process-name">{process.name}</h1>
              <p className="process-description">{process.description}</p>
              <button
                onClick={() => setIsEditing(true)}
                className="edit-btn"
                title="Edit process name and description"
              >
                ✏️
              </button>
            </div>
          )}
        </div>

        <div className="process-owner">
          <span className="owner-label">Owner:</span>
          <span className="owner-artifact">{process.prime_artifact_ref}</span>
        </div>
      </div>

      <div className="header-actions">
        <button
          onClick={() => setShowParametersDialog(true)}
          className="parameters-btn"
          title="Edit process parameters"
        >
          ⚙️ Parameters
        </button>

        <button
          onClick={onToggleValidation}
          className={`validation-btn ${validationResults?.error_count ? 'has-errors' : ''}`}
          title="Toggle validation panel"
        >
          {getValidationIcon()}
          <span className="validation-text">{getValidationText()}</span>
        </button>

        <button
          onClick={onToggleNavigation}
          className="navigation-btn"
          title="Toggle navigation panel"
        >
          🧭 Navigation
        </button>
      </div>

      {showParametersDialog && (
        <ProcessParametersDialog
          process={process}
          onSave={(parameters) => {
            onProcessUpdate({ process_parameters: parameters });
            setShowParametersDialog(false);
          }}
          onCancel={() => setShowParametersDialog(false)}
        />
      )}
    </div>
  );
};
